import { ButtonHTMLAttributes, ReactNode } from "react";
import { X } from "lucide-react";
import styles from "./Badge.module.css";
import { cx } from "../utils";

export interface BadgeProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: "status" | "tag" | "count" | "pill";
    tone?: "success" | "warning" | "error" | "info" | "neutral" | "accent";
    size?: "sm" | "md";
    icon?: ReactNode;
    onRemove?: () => void;
    active?: boolean;
    children: ReactNode;
}

const sizeClasses = {
    sm: styles.sizeSm,
    md: styles.sizeMd
};

const variantClasses = {
    status: styles.status,
    tag: "",
    count: styles.count,
    pill: styles.pill
};

const toneClasses = {
    success: styles.toneSuccess,
    warning: styles.toneWarning,
    error: styles.toneError,
    info: styles.toneInfo,
    neutral: styles.toneNeutral,
    accent: styles.toneAccent
};

export const Badge = ({
    variant = "tag",
    tone = "neutral",
    size = "md",
    icon,
    onRemove,
    active = false,
    children,
    className,
    onClick,
    type = "button",
    ...props
}: BadgeProps) => {
    const isInteractive = Boolean(onClick);
    const sharedClassName = cx(styles.badge, sizeClasses[size], variantClasses[variant], toneClasses[tone], active && styles.active, className);

    const content = (
        <>
            {icon ? <span className={styles.icon}>{icon}</span> : null}
            <span>{children}</span>
            {onRemove ? (
                <button
                    type="button"
                    className={styles.removeButton}
                    onClick={event => {
                        event.stopPropagation();
                        onRemove();
                    }}
                    aria-label="Remove"
                >
                    <X size={12} />
                </button>
            ) : null}
        </>
    );

    if (isInteractive) {
        return (
            <button type={type} className={cx(sharedClassName, styles.button)} onClick={onClick} {...props}>
                {content}
            </button>
        );
    }

    return <span className={sharedClassName}>{content}</span>;
};
